import { useEffect, useState } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import type { mostUsedSitesQuery } from '#generated/graphql/mostUsedSitesQuery.graphql';
import {
  rankMostUsedSites,
  type RovoMostUsedSite,
} from './mostUsedSitesRanking';

const query = graphql`
  query mostUsedSitesQuery {
    activity {
      myActivity {
        viewed(
          first: 50
          filter: {
            arguments: {
              products: ["Confluence", "Jira"]
              objectTypes: ["issue", "page", "blogpost"]
            }
          }
        ) {
          edges {
            node {
              object {
                rootContainerId
              }
            }
          }
        }
      }
    }
  }
`;

export function useRovoMostUsedSites(
  enabled: boolean,
): UseRovoMostUsedSitesResult {
  const deprecated = useFeatureGate(
    OAuthConsentFeatureGate.DEPRECATE_MOST_USED_SITES,
  );
  const effectiveEnabled = enabled && !deprecated;
  const environment = useRelayEnvironment();
  const [mostUsedSites, setMostUsedSites] = useState<RovoMostUsedSite[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!effectiveEnabled) {
      setMostUsedSites([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const subscription = fetchQuery<mostUsedSitesQuery>(
      environment,
      query,
      {},
      { fetchPolicy: 'store-or-network' },
    ).subscribe({
      next: (data) => {
        const edges = data.activity?.myActivity?.viewed.edges ?? [];
        setMostUsedSites(
          rankMostUsedSites(
            edges.map(({ node: { object } }) => object.rootContainerId),
          ),
        );
      },
      error: () => {
        setMostUsedSites([]);
        setIsLoading(false);
      },
      complete: () => {
        setIsLoading(false);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [effectiveEnabled, environment]);

  return { mostUsedSites, isLoading, deprecated };
}

export type UseRovoMostUsedSitesResult = {
  mostUsedSites: RovoMostUsedSite[];
  isLoading: boolean;
  deprecated: boolean;
};

export type { RovoMostUsedSite };
